import React, {ChangeEventHandler, FocusEventHandler, useCallback, useEffect, useState} from 'react';

import cn from 'classnames';
import {ColorState, SketchPicker} from 'react-color';
import {useToggle} from 'react-use';

import bem from 'client/services/bem';
import {onlyPositiveNumbers} from 'client/services/formatters';
import {useLanguage} from 'client/services/hooks';
import {addAlphaToHex, getAlphaPercentFromHex, isValidHex, rgbaToHex} from 'client/services/utils/color';

import AppButton from 'client/common/buttons/app-button';
import {ErrorMessage, RequiredLabel, WarningMessage} from 'client/common/inputs';

import {ColorPickerInputProps} from './type';

import cssModule from './color-picker-input.module.scss';

const b = bem('color-picker-input', {cssModule});

const getOpacity = (hexValue: string) => {
  if (hexValue.length <= 7) {
    return 255;
  }
  return parseInt(hexValue.slice(-2), 16);
};

const ColorPickerInput: React.FC<ColorPickerInputProps> = (props) => {
  const {
    name,
    onChange,
    className = '',
    classNames = {},
    label = '',
    value = '',
    errorMessage = '',
    warningMessage = '',
    disabled = false,
    hexWithAlpha = false,
    disableManualInput = false,
    inputValue = '',
    customColorBox = null,
    hideValue = false,
    required = false,
    inputHeight = 'default',
    flipControls = false,
    showAlphaInput = false,
    labelColor = 'default',
    shortAlphaInput = false,
    compact = false,
    predefinedValue = '',
    textColor = 'default',
    ...inputProps
  } = props;
  const lang = useLanguage('COMMON.INPUTS.COLOR_PICKER_INPUT');
  const [open, toggleOpen] = useToggle(false);
  const [colorInput, setColorInput] = useState((inputValue || value || '').slice(0, 7));
  const [alphaInput, setAlphaInput] = useState(getAlphaPercentFromHex(value || ''));
  const [pickerData, setPickerData] = useState<ColorState | null>(null);

  const predefinedColor = predefinedValue ? predefinedValue.slice(0, 7) : '';
  const predefinedAlpha = predefinedValue ? (100 - getAlphaPercentFromHex(predefinedValue)).toString() : '';

  const alphaInputDisplayedValue = 100 - alphaInput;

  const mergeColorAndAlpha = useCallback(
    (color: string, alpha: number) => {
      let result = color.slice(0, 7);
      if (!result || isValidHex(result)) {
        if (hexWithAlpha) {
          result = addAlphaToHex(result, alpha);
        }
        return result;
      }
      return '';
    },
    [hexWithAlpha],
  );

  useEffect(() => {
    setColorInput((inputValue || value || '').slice(0, 7));
    setAlphaInput(getAlphaPercentFromHex(value || ''));
    setPickerData(null);
  }, [inputValue, value]);

  const handleAlphaChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const targetValue = Number(onlyPositiveNumbers(event.target.value));
      const result = targetValue > 100 ? 100 : targetValue;
      if (!colorInput && predefinedColor) {
        setColorInput(predefinedColor);
      }
      const alpha = 100 - result;

      setAlphaInput(alpha);
      const nextColor = mergeColorAndAlpha(colorInput, alpha);
      if (nextColor !== value && isValidHex(nextColor)) {
        onChange?.(nextColor);
      }
    },
    [colorInput, mergeColorAndAlpha, onChange, predefinedColor, value],
  );

  const handleColorChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const targetValue = event.target.value;
      const color = targetValue.slice(0, 7);
      setColorInput(targetValue);
      let realAlpha = alphaInput;
      if (!color) {
        setAlphaInput(100);
        realAlpha = 100;
      }

      const nextColor = mergeColorAndAlpha(color, realAlpha);
      if (nextColor !== value && (isValidHex(nextColor) || !color)) {
        onChange?.(nextColor);
      }
    },
    [alphaInput, mergeColorAndAlpha, onChange, value],
  );

  const handleColorBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const blurValue = event.target.value;
      inputProps?.onBlur?.(event);
      if (!isValidHex(blurValue)) {
        setColorInput(predefinedColor);
        onChange?.(predefinedColor);
      } else {
        const nextColor = hexWithAlpha ? addAlphaToHex(blurValue, alphaInput) : blurValue;
        setColorInput(nextColor);
        onChange?.(nextColor);
      }
    },
    [alphaInput, hexWithAlpha, inputProps, onChange, predefinedColor],
  );

  const handlePickerChange = useCallback(
    (colorState: ColorState) => {
      setPickerData(colorState);
      const hex = colorState.hex.toUpperCase();
      const hexAndAlpha = rgbaToHex(colorState.rgb);
      setColorInput(hex);
      setAlphaInput(getAlphaPercentFromHex(hexAndAlpha));
      onChange?.(hexWithAlpha ? hexAndAlpha : hex);
    },
    [hexWithAlpha, onChange],
  );

  const opacity = value && disableManualInput ? Math.round((getOpacity(value) * 100) / 255) : 0;

  return (
    <div className={cn(b({error: !!errorMessage, warning: !!warningMessage, compact}), className)}>
      {label && (
        <label htmlFor={name} className={b('label', [labelColor])}>
          {label}
        </label>
      )}
      {showAlphaInput && (
        <label htmlFor={`${name}-alpha`} className={b('label', ['alpha', labelColor])}>
          {shortAlphaInput ? lang.TRANSPARENCY_SHORT : lang.TRANSPARENCY}
        </label>
      )}
      <div className={b('controls', {flip: flipControls, 'small-gap': shortAlphaInput})}>
        <AppButton
          asWrap={true}
          onClick={disabled ? () => {} : () => toggleOpen()}
          label={
            customColorBox || (
              <div className={b('color-box', [`height-${inputHeight}`, 'wrap'])}>
                <div
                  className={b('color-box', [`height-${inputHeight}`])}
                  style={{backgroundColor: value || predefinedValue}}
                />
              </div>
            )
          }
        />
        {open && (
          <div className={b('popover')}>
            <div className={b('cover')} onClick={toggleOpen} />
            <SketchPicker
              color={pickerData?.rgb || value || predefinedValue}
              onChangeComplete={handlePickerChange}
              disableAlpha={!hexWithAlpha}
              presetColors={[]}
            />
          </div>
        )}
        {disableManualInput ? (
          <p className={b('color-code')}>
            <span className={b('code-value', {hide: hideValue})}>{inputValue || value}</span>
            {!customColorBox && opacity && <span>{`${lang.OPACITY}: ${opacity} %`}</span>}
          </p>
        ) : (
          <>
            {showAlphaInput && (
              <input
                placeholder={value ? '0' : predefinedAlpha.toString()}
                className={cn(
                  b('code-input', {
                    alpha: true,
                    hide: hideValue,
                    [`height-${inputHeight}`]: true,
                    short: shortAlphaInput,
                    [`text-color-${textColor}`]: true,
                  }),
                  classNames.input,
                )}
                type="text"
                name={`${name}-alpha`}
                value={alphaInputDisplayedValue ? alphaInputDisplayedValue : ''}
                onChange={handleAlphaChange}
                aria-invalid={!!errorMessage}
              />
            )}
            <input
              disabled={disabled}
              {...inputProps}
              placeholder={predefinedColor || inputProps.placeholder}
              onBlur={handleColorBlur}
              className={cn(
                b('code-input', {
                  hide: hideValue,
                  [`height-${inputHeight}`]: true,
                  [`text-color-${textColor}`]: true,
                }),
                classNames.input,
              )}
              type="text"
              name={name}
              value={colorInput.slice(0, 7)}
              onChange={handleColorChange}
              maxLength={7}
              aria-invalid={!!errorMessage}
            />
          </>
        )}
      </div>
      {!errorMessage && required && <RequiredLabel />}
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      {warningMessage && !errorMessage && <WarningMessage warningMessage={warningMessage} />}
    </div>
  );
};

export default ColorPickerInput;
